import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Alert, Divider, List, ListItem, ListItemIcon, ListItemText, Chip, Table, TableBody, TableCell, TableHead, TableRow, Skeleton, CircularProgress } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Toll as CreditsIcon,
  CardGiftcard as FreeIcon,
  ShoppingCart as PurchaseIcon,
  People as ReferralIcon,
  CreditCard as PaymentIcon,
  Celebration as BonusIcon,
  Stars as TierIcon,
  Layers as NumberSetsIcon,
} from '@mui/icons-material';
import { SupportLayout, InfoCard, VisualExample } from '../../components/support';
import { useDocumentTitle } from '../../hooks/core';
import { useTierConfig } from '../../hooks';
import { productsApi, type PaymentProduct } from '../../api';
import { publicPaymentApi } from '../../api/billing';
import { referralApi, type ReferralRewardTier } from '../../api/referral';

// Helper to get tier color
const getTierColor = (tierName: string): { bgcolor: string; color: string } => {
  const name = tierName.toLowerCase();
  if (name.includes('platinum')) return { bgcolor: '#1976d2', color: 'white' };
  if (name.includes('gold')) return { bgcolor: '#ffd700', color: 'black' };
  if (name.includes('silver')) return { bgcolor: '#c0c0c0', color: 'black' };
  if (name.includes('bronze')) return { bgcolor: '#cd7f32', color: 'white' };
  return { bgcolor: 'grey.500', color: 'white' };
};

// Helper to format tier requirements as readable range
const getTierRange = (tier: ReferralRewardTier, allTiers: ReferralRewardTier[], index: number): string => {
  const nextTier = allTiers[index + 1];
  if (nextTier) {
    return `${tier.minReferrals}-${nextTier.minReferrals - 1} referrals`;
  }
  return `${tier.minReferrals}+ referrals`;
};

export const CreditsSupportPage: React.FC = () => {
  useDocumentTitle('Credits & Payments - Support');

  const [loading, setLoading] = useState(true);
  const [freeCredits, setFreeCredits] = useState<number>(2);
  const [creditPacks, setCreditPacks] = useState<PaymentProduct[]>([]);
  const [referralTiers, setReferralTiers] = useState<ReferralRewardTier[]>([]);

  // Tier config for board pricing
  const { configs: tierConfigs, isLoading: tierConfigLoading } = useTierConfig();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [products, paymentConfig, tiers] = await Promise.all([
          productsApi.listCreditPacks(),
          publicPaymentApi.getPublicConfig(),
          referralApi.getTiers(),
        ]);

        // Sort credit packs by credits ascending
        const sorted = products.sort((a, b) => {
          const aCredits = a.metadata?.boardCredits || 0;
          const bCredits = b.metadata?.boardCredits || 0;
          return aCredits - bCredits;
        });
        setCreditPacks(sorted);
        setFreeCredits(paymentConfig.freeCreditsForNewUsers);

        // Sort referral tiers by minReferrals
        const sortedTiers = tiers.sort((a, b) => a.minReferrals - b.minReferrals);
        setReferralTiers(sortedTiers);
      } catch (error) {
        console.error('Failed to fetch credits data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Helper to get label for credit pack
  const getCreditPackLabel = (index: number, total: number): string => {
    if (total <= 1) return '';
    if (index === 0) return 'STARTER';
    if (index === total - 1) return 'POWER USER';
    return 'BEST VALUE';
  };

  // Check if this pack is the "best value" (lowest cost per board)
  const isBestValue = (pack: PaymentProduct, allPacks: PaymentProduct[]): boolean => {
    if (allPacks.length <= 1) return false;

    const packCredits = pack.metadata?.boardCredits || 1;
    const packPrice = pack.prices?.[0]?.amount || 0;
    const packCostPerBoard = packPrice / packCredits;

    return allPacks.every(p => {
      const pCredits = p.metadata?.boardCredits || 1;
      const pPrice = p.prices?.[0]?.amount || 0;
      const pCostPerBoard = pPrice / pCredits;
      return packCostPerBoard <= pCostPerBoard;
    });
  };

  return (
    <SupportLayout
      title="Credits & Payments"
      description="Understand how board credits work, purchase options, and the referral program."
      breadcrumbs={[{ label: 'Credits & Payments' }]}
    >
      {/* Overview */}
      <Box id="how-credits-work" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <CreditsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          How Credits Work
        </Typography>

        <Typography variant="body1" paragraph>
          SquareUp uses a <strong>credit system</strong> for board creation. The number of credits required depends on the board tier you choose. Credits never expire and can be used anytime.
        </Typography>

        <Paper elevation={0} sx={{ p: 4, bgcolor: 'primary.50', border: 1, borderColor: 'primary.200' }}>
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <CreditsIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">1-4 Credits</Typography>
                  <Typography variant="body2" color="text.secondary">per Board (based on tier)</Typography>
                </Box>
              </Box>
              <Typography variant="body1">
                When you create a board, credits are deducted based on your chosen number set option. Standard boards cost 1 credit; premium options cost more.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, bgcolor: 'white' }}>
                <Typography variant="h6" gutterBottom>What You Get</Typography>
                <List dense>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                    <ListItemText primary="Full sports squares board" />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                    <ListItemText primary="Unlimited participants" />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                    <ListItemText primary="All payout features" />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                    <ListItemText primary="Complete accounting" />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                    <ListItemText primary="Board export & sharing" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Board Tiers & Pricing */}
      <Box id="board-tiers" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <NumberSetsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Board Tiers & Pricing
        </Typography>

        <Typography variant="body1" paragraph>
          Board creation costs vary based on the <strong>number set complexity</strong> you choose. Premium number set options require more credits but offer additional variety in your pools.
        </Typography>

        <Grid container spacing={3}>
          {tierConfigLoading ? (
            <Grid size={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            </Grid>
          ) : tierConfigs.length === 0 ? (
            <Grid size={12}>
              <Alert severity="info">Tier pricing information is not available.</Alert>
            </Grid>
          ) : (
            tierConfigs.map((config) => (
              <Grid size={{ xs: 12, sm: 4 }} key={config.id}>
                <Paper
                  elevation={config.numberMode === 'single' ? 1 : 3}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    height: '100%',
                    border: 2,
                    borderColor: config.numberMode === 'quarter' ? 'secondary.main' : config.numberMode === 'half' ? 'primary.main' : 'grey.300',
                    bgcolor: config.numberMode !== 'single' ? 'grey.50' : 'white',
                  }}
                >
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color={config.numberMode === 'quarter' ? 'secondary.main' : config.numberMode === 'half' ? 'primary.main' : 'text.primary'}
                    gutterBottom
                  >
                    {config.creditCost}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Credit{config.creditCost !== 1 ? 's' : ''}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {config.displayName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {config.description || (
                      config.numberMode === 'single'
                        ? 'Same random numbers throughout the game'
                        : config.numberMode === 'half'
                          ? 'Different numbers for each half'
                          : 'Unique numbers for each quarter/period'
                    )}
                  </Typography>
                  {config.numberMode !== 'single' && (
                    <Chip
                      label="Premium"
                      color={config.numberMode === 'quarter' ? 'secondary' : 'primary'}
                      size="small"
                      sx={{ mt: 2 }}
                    />
                  )}
                </Paper>
              </Grid>
            ))
          )}
        </Grid>

        <Alert severity="info" sx={{ mt: 3 }}>
          All boards include the same features regardless of tier—unlimited participants, all payout options, and complete accounting. The tier simply determines how random numbers are generated.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Free Credits */}
      <Box id="free-credits" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <FreeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Free Credits for New Users
        </Typography>

        <Typography variant="body1" paragraph>
          Every new SquareUp account starts with <strong>free board credits</strong>. This lets you try the platform and run your first pools without any upfront cost.
        </Typography>

        <Paper elevation={0} sx={{ p: 4, bgcolor: 'success.50', border: 2, borderColor: 'success.main', textAlign: 'center' }}>
          <BonusIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
          {loading ? (
            <Skeleton variant="text" width={200} height={60} sx={{ mx: 'auto' }} />
          ) : (
            <Typography variant="h3" fontWeight="bold" color="success.main">
              {freeCredits} Free Credit{freeCredits !== 1 ? 's' : ''}
            </Typography>
          )}
          <Typography variant="body1" color="text.secondary">
            Included with every new account
          </Typography>
        </Paper>

        <Alert severity="info" sx={{ mt: 3 }}>
          Free credits work exactly like purchased credits—there are no limitations or watermarks on boards created with free credits.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Credit Packs */}
      <Box id="credit-packs" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <PurchaseIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Purchasing Credits
        </Typography>

        <Typography variant="body1" paragraph>
          Need more boards? Purchase credit packs from the Credits page. The more credits you buy, the better the per-board price.
        </Typography>

        <Grid container spacing={3}>
          {loading ? (
            // Loading skeleton
            [1, 2, 3].map((i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%', textAlign: 'center' }}>
                  <Skeleton variant="rounded" width={80} height={24} sx={{ mx: 'auto', mb: 2 }} />
                  <Skeleton variant="text" width={60} height={60} sx={{ mx: 'auto' }} />
                  <Skeleton variant="text" width={80} sx={{ mx: 'auto', mb: 1 }} />
                  <Skeleton variant="text" width={100} sx={{ mx: 'auto' }} />
                </Paper>
              </Grid>
            ))
          ) : creditPacks.length > 0 ? (
            creditPacks.map((pack, index) => {
              const credits = pack.metadata?.boardCredits || 1;
              const price = pack.prices?.[0];
              const priceAmount = price ? (price.amount / 100) : 0;
              const pricePerBoard = credits > 0 ? priceAmount / credits : priceAmount;
              const label = getCreditPackLabel(index, creditPacks.length);
              const bestValue = isBestValue(pack, creditPacks);

              // Calculate savings compared to first pack
              const firstPack = creditPacks[0];
              const firstPrice = firstPack?.prices?.[0];
              const firstPricePerBoard = firstPrice && firstPack?.metadata?.boardCredits
                ? (firstPrice.amount / 100) / firstPack.metadata.boardCredits
                : pricePerBoard;
              const savingsPercent = firstPricePerBoard > pricePerBoard
                ? Math.round((1 - pricePerBoard / firstPricePerBoard) * 100)
                : 0;

              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={pack.id}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      border: bestValue ? 2 : 1,
                      borderColor: bestValue ? 'primary.main' : 'divider',
                      height: '100%',
                      textAlign: 'center',
                      position: 'relative',
                    }}
                  >
                    {bestValue ? (
                      <Chip
                        label={label}
                        color="primary"
                        size="small"
                        sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)' }}
                      />
                    ) : label ? (
                      <Chip label={label} size="small" sx={{ mb: 2 }} />
                    ) : null}
                    <Typography variant="h3" color="primary.main" fontWeight="bold" sx={{ mt: bestValue ? 2 : 0 }}>
                      {credits}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      credit{credits !== 1 ? 's' : ''}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
                      ${priceAmount.toFixed(2)}
                    </Typography>
                    {savingsPercent > 0 ? (
                      <Typography variant="body2" color="success.main" fontWeight="bold">
                        ${pricePerBoard.toFixed(2)}/board - Save {savingsPercent}%
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        ${pricePerBoard.toFixed(2)}/board
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              );
            })
          ) : (
            // Fallback if no packs available
            <Grid size={12}>
              <Alert severity="info">Credit packs are currently being configured. Please check back later.</Alert>
            </Grid>
          )}
        </Grid>

        <InfoCard title="Credits Never Expire" variant="tip" icon={<CreditsIcon />}>
          <Typography variant="body2">
            Unlike subscriptions, your credits <strong>never expire</strong>. Buy them when you need them, use them whenever you want. Perfect for seasonal pool runners who only need boards a few times a year.
          </Typography>
        </InfoCard>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Payment Methods */}
      <Box id="payment-methods" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <PaymentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Payment Methods
        </Typography>

        <Typography variant="body1" paragraph>
          SquareUp uses Stripe for secure payment processing. We accept:
        </Typography>

        <Grid container spacing={2}>
          {[
            { name: 'Credit Cards', desc: 'Visa, Mastercard, Amex, Discover' },
            { name: 'Debit Cards', desc: 'Any debit card with Visa/MC logo' },
            { name: 'Apple Pay', desc: 'On supported devices' },
            { name: 'Google Pay', desc: 'On supported devices' },
          ].map((method) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={method.name}>
              <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', textAlign: 'center', height: '100%' }}>
                <Typography variant="subtitle2" fontWeight="bold">{method.name}</Typography>
                <Typography variant="caption" color="text.secondary">{method.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Alert severity="success" sx={{ mt: 3 }}>
          <strong>Secure Payments:</strong> All payment information is handled securely by Stripe. SquareUp never stores your credit card details.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Referral Program */}
      <Box id="referral-program" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <ReferralIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Referral Program
        </Typography>

        <Typography variant="body1" paragraph>
          Earn free credits by referring friends! When someone signs up using your referral link and purchases credits, you both earn bonus credits.
        </Typography>

        <VisualExample title="How Referrals Work" caption="Share your unique referral link to start earning">
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', textAlign: 'center' }}>
                <Typography variant="h6" color="primary.main">Step 1</Typography>
                <Typography variant="body2">Share your referral link</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', textAlign: 'center' }}>
                <Typography variant="h6" color="primary.main">Step 2</Typography>
                <Typography variant="body2">Friend signs up & buys credits</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 2, bgcolor: 'success.50', textAlign: 'center' }}>
                <Typography variant="h6" color="success.main">Step 3</Typography>
                <Typography variant="body2" fontWeight="bold">You both earn bonus credits!</Typography>
              </Paper>
            </Grid>
          </Grid>
        </VisualExample>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            <TierIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Referral Tiers
          </Typography>
          <Typography variant="body2" paragraph>
            The more friends you refer, the more you earn per referral:
          </Typography>

          <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tier</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Referrals Required</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Your Bonus</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  // Loading skeleton
                  [1, 2, 3, 4].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="rounded" width={80} height={24} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                    </TableRow>
                  ))
                ) : referralTiers.length > 0 ? (
                  referralTiers.map((tier, index) => {
                    const colors = getTierColor(tier.name);
                    const isTopTier = index === referralTiers.length - 1;
                    return (
                      <TableRow key={tier.id} sx={isTopTier ? { bgcolor: 'primary.50' } : undefined}>
                        <TableCell>
                          <Chip
                            label={tier.name}
                            size="small"
                            sx={colors.bgcolor.startsWith('#')
                              ? { bgcolor: colors.bgcolor, color: colors.color }
                              : undefined
                            }
                            color={!colors.bgcolor.startsWith('#') ? 'primary' : undefined}
                          />
                        </TableCell>
                        <TableCell>{getTierRange(tier, referralTiers, index)}</TableCell>
                        <TableCell>{tier.creditsAwarded} credit{tier.creditsAwarded !== 1 ? 's' : ''}</TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  // Fallback if no tiers available
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Alert severity="info" sx={{ m: 0 }}>Referral tiers are currently being configured.</Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Box>

        <InfoCard title="Find Your Referral Link" variant="info" icon={<ReferralIcon />}>
          <Typography variant="body2">
            Your unique referral link can be found in <strong>Profile → My Referrals</strong>. Copy and share it via text, email, or social media. When someone clicks it and signs up, they're automatically tracked as your referral.
          </Typography>
        </InfoCard>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Credit Balance */}
      <Box id="credit-balance" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Checking Your Credit Balance
        </Typography>

        <Typography variant="body1" paragraph>
          Your current credit balance is always visible in the navigation bar. Click it to go to the Credits page where you can purchase more.
        </Typography>

        <VisualExample title="Credit Balance Display" caption="Your credits are shown in the header navigation">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, alignItems: 'center' }}>
            <Paper sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.100' }}>
              <CreditsIcon sx={{ color: 'primary.main' }} />
              <Typography variant="body1" fontWeight="bold">5 credits</Typography>
            </Paper>
            <Typography variant="body2" color="text.secondary">
              ← Click to purchase more
            </Typography>
          </Box>
        </VisualExample>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* FAQ */}
      <Box id="credits-faq" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Common Questions
        </Typography>

        <Grid container spacing={2}>
          {[
            {
              q: 'What happens if I delete a board?',
              a: 'Credits used to create deleted boards are not refunded. However, boards in "draft" status that are deleted do return the credit.',
            },
            {
              q: 'Can I transfer credits to someone else?',
              a: 'Currently, credits cannot be transferred between accounts. Each account manages its own credits.',
            },
            {
              q: 'Is there a subscription option?',
              a: 'Subscription plans may be available in the future. Currently, all credits are one-time purchases.',
            },
            {
              q: 'Can I get a refund on credits?',
              a: 'Unused credits can be refunded within 30 days of purchase. Contact support for refund requests.',
            },
          ].map((item, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  {item.q}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.a}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </SupportLayout>
  );
};

export default CreditsSupportPage;
